import { requestApi } from 'api/api'
import { v4 as uuidv4 } from 'uuid'
import {
  AddCustomerWalletRequest,
  Application,
  AssignCustomerFeeWalletPayload,
  AssignCustomerFeeWalletResponse,
  BusinessCustomerResponse,
  CreateBusinessCustomer,
  CustomerApplicationDetails,
  CustomerApplicationStatusDetails,
  CustomersRequest,
  CustomerWalletProfile,
  CustomerWalletsRequest,
  CustomerWalletsV2Response,
  CustomerWalletV2Individual,
  GetCustomerFeeWalletsResponse,
  GetCustomerWalletProfilesRequest,
  Industries,
  MonthlyVolume,
  WalletResponse,
} from 'types/customers'
import { WithPaginationApiResponse } from 'types/pagination'
import { CountryState } from 'types/types'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

export const CUSTOMER_API_URL = '/platform/v1/customers'

export const CUSTOMER_V2_API_URL = '/onboarding/v1/applications'

const LUCENE_SPECIAL_CHARS = /["\\]/g

const escapeSearchQueryTerm = (term: string): string =>
  term.replaceAll(LUCENE_SPECIAL_CHARS, char => `\\${char}`)

export const getCustomers = <CustomerSummary extends object>({
  search,
  size,
  page,
  sort,
  filters,
}: CustomersRequest): Promise<WithPaginationApiResponse<CustomerSummary[]>> => {
  return requestApi({
    url: CUSTOMER_API_URL,
    method: 'GET',
    params: {
      size,
      page,
      ...(sort && { sort }),
      ...(search && { name: search }),
      ...(filters && { ...filters }),
    },
  })
}

export const postAddCustomer = <CustomerType extends object>(
  customer: CustomerType
): Promise<void> => {
  return requestApi({
    url: CUSTOMER_API_URL,
    method: 'POST',
    data: customer,
  })
}

export const getCustomer = <CustomerDetails extends object>(
  customerReference: string
): Promise<CustomerDetails> => {
  return requestApi({
    url: `${CUSTOMER_API_URL}/${customerReference}`,
    method: 'GET',
  })
}

export const patchCustomer = <CustomerPatchData extends object>({
  customerReference,
  data,
}: {
  customerReference: string
  data: CustomerPatchData
}): Promise<void> => {
  return requestApi({
    url: `${CUSTOMER_API_URL}/${customerReference}`,
    method: 'PATCH',
    data,
  })
}

export const getAllMonthlyVolumes = (): Promise<MonthlyVolume[]> => {
  return requestApi({
    url: '/platform/v1/accounts/monthly-expected-volumes',
    method: 'GET',
  })
}

export const getIndustries = (): Promise<Industries[]> => {
  return requestApi({
    url: '/platform/v1/accounts/industries',
    method: 'GET',
  })
}

export const getCountryStates = (countryCode: string): Promise<CountryState[]> => {
  return requestApi({
    url: `/platform/v1/accounts/country/${countryCode}/states`,
    method: 'GET',
    baseUrl: process.env.API_URL,
  })
}

export const postAddCustomerWallet = (
  wallet: AddCustomerWalletRequest
): Promise<WalletResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v1/wallets',
    method: 'POST',
    data: wallet,
    headers: {
      'x-idempotency-key': uuidv4(),
    },
  })
}

export const getCustomerWallets = ({
  customerReference,
  currencyCodeSearch,
  currencyCodes,
  currencyType,
  searchTerm,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  statuses,
}: CustomerWalletsRequest): Promise<CustomerWalletsV2Response> => {
  const currencyQuery = currencyCodes?.length
    ? `currency:(${currencyCodes.join(' OR ')})`
    : currencyCodeSearch && `currency:${currencyCodeSearch}`
  const searchQuery = [
    customerReference && `customerId:${customerReference}`,
    currencyType && `currencyType:${currencyType}`,
    currencyQuery,
    searchTerm && `name:"${escapeSearchQueryTerm(searchTerm)}"`,
    statuses?.length && `status:(${statuses.join(' OR ')})`,
  ]
    .filter(Boolean)
    .join(' AND ')

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v2/wallets',
    method: 'GET',
    params: {
      ...(searchQuery && { q: searchQuery }),
      pageSize: size,
      pageNumber: page,
      ...(sort && { sort }),
    },
  })
}

export const getCustomerWallet = (walletId: string): Promise<CustomerWalletV2Individual> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/ledger/v2/wallets/${walletId}`,
    method: 'GET',
  })
}

export const getCustomerWalletProfiles = (
  request?: GetCustomerWalletProfilesRequest
): Promise<CustomerWalletProfile> => {
  const { currencyCodes, paymentMethods } = request || {}

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v1/wallets/profiles',
    method: 'GET',
    params: {
      ...(currencyCodes && { currencyCodes: currencyCodes?.join(',')?.toUpperCase() }),
      ...(paymentMethods && { paymentMethods: paymentMethods?.join(',')?.toLowerCase() }),
    },
  })
}

export const assignCustomerFeeWallet = (
  payload: AssignCustomerFeeWalletPayload
): Promise<AssignCustomerFeeWalletResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/fees/customer-fee-wallets',
    method: 'PUT',
    data: payload,
  })
}

export const getCustomerFeeWallets = (): Promise<GetCustomerFeeWalletsResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/fees/customer-fee-wallets',
    method: 'GET',
  })
}

export const getApplications = (): Promise<Application[]> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: CUSTOMER_V2_API_URL,
    method: 'GET',
  })
}

export const postAddBusinessCustomer = (
  customer: CreateBusinessCustomer
): Promise<BusinessCustomerResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: CUSTOMER_V2_API_URL,
    method: 'POST',
    data: customer,
  })
}

export const getCustomerApplication = (
  onboardingId: string
): Promise<CustomerApplicationDetails> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `${CUSTOMER_V2_API_URL}/${onboardingId}`,
    method: 'GET',
  })
}

export const getCustomerStatus = (
  onboardingId: string
): Promise<CustomerApplicationStatusDetails> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `${CUSTOMER_V2_API_URL}/${onboardingId}/status`,
    method: 'GET',
  })
}
